import React, { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';
import StoreService from '../../components/StoreService';
import ProductService from '../../components/ProductService';
import { getTrackingByStore } from '../../components/TrackingService';

function GlovoTracking() {
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [trackingData, setTrackingData] = useState([]);
  const [localProducts, setLocalProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [filterActive, setFilterActive] = useState('all'); // 'all', 'active', 'inactive'
  const [filterSynced, setFilterSynced] = useState('all'); // 'all', 'synced', 'unsynced'
  const [filterComponents, setFilterComponents] = useState([]); // Array of selected composant IDs
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [searchName, setSearchName] = useState('');

  // Modal state
  const [showComposantsModal, setShowComposantsModal] = useState(false);
  const [selectedTracking, setSelectedTracking] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Load stores and local products once on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load stores
        const storesData = await StoreService.fetchStores();
        const storeList = storesData.response || storesData;
        setStores(Array.isArray(storeList) ? storeList : []);

        // Load local products once for comparison
        const productsResponse = await ProductService.fetchProducts();
        const productsList = productsResponse.response || productsResponse;
        setLocalProducts(Array.isArray(productsList) ? productsList : []);
      } catch (err) {
        console.error('Error loading initial data:', err);
      }
    };
    loadInitialData();
  }, []);

  // Load tracking data when store is selected
  useEffect(() => {
    if (!selectedStoreId) return;

    const loadTrackingData = async () => {
      try {
        setLoading(true);
        setError('');

        // Load tracking records
        const trackingResponse = await getTrackingByStore(selectedStoreId);
        const trackingRecords = trackingResponse.data || trackingResponse.response || [];
        setTrackingData(Array.isArray(trackingRecords) ? trackingRecords : []);
      } catch (err) {
        console.error('Error loading tracking data:', err);
        setError(err?.response?.data?.error || err.message || 'Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadTrackingData();
  }, [selectedStoreId]);

  // Store options for select
  const storeOptions = useMemo(() => {
    return stores.map(store => ({
      value: store._id || store.id,
      label: store.name || store.code || (store._id || store.id)
    }));
  }, [stores]);

  // Composant options for multi-select
  const composantOptions = useMemo(() => {
    return localProducts
      .filter(p => p.isComposant === true)
      .map(composant => ({
        value: composant._id || composant.id,
        label: composant.name,
        image: composant.url_product ? `${process.env.REACT_APP_DATA_PIC_PATH}/${composant.url_product}` : null,
        price: composant.regular_price || 0
      }));
  }, [localProducts]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const activeCount = trackingData.filter(item => item.isactive === true).length;
    const inactiveCount = trackingData.filter(item => item.isactive === false).length;
    const syncedCount = trackingData.filter(item => item.synced === true).length;
    
    // Anomalies: tracking price < product price
    const anomaliesCount = trackingData.filter(item => {
      if (!item.product_id) return false;
      const productId = item.product_id?.$oid || item.product_id?._id || item.product_id;
      const localProduct = localProducts.find(p => (p._id || p.id) === productId);
      if (!localProduct) return false;
      return item.price < (localProduct.regular_price || 0);
    }).length;

    return {
      active: activeCount,
      inactive: inactiveCount,
      synced: syncedCount,
      anomalies: anomaliesCount
    };
  }, [trackingData, localProducts]);

  // Filtered data
  const filteredData = useMemo(() => {
    let filtered = [...trackingData];

    // Filter by active status
    if (filterActive === 'active') {
      filtered = filtered.filter(item => item.isactive === true);
    } else if (filterActive === 'inactive') {
      filtered = filtered.filter(item => item.isactive === false);
    }

    // Filter by synced status
    if (filterSynced === 'synced') {
      filtered = filtered.filter(item => item.synced === true);
    } else if (filterSynced === 'unsynced') {
      filtered = filtered.filter(item => item.synced === false);
    }

    // Filter by selected composants
    // Find all products that have the selected composants in their components array
    // Then show tracking items whose product_id matches those products
    if (filterComponents.length > 0) {
      // Gather all product IDs that have the selected composants
      const productIdsWithSelectedComposants = [];
      
      localProducts.forEach(product => {
        if (!product.composents || !Array.isArray(product.composents)) return;
        
        // Check if this product has any of the selected composants
        const hasSelectedComposant = product.composents.some(composent => {
          // Extract the product field from composent (could be object or string)
          const composentProductId = composent.product?.$oid || composent.product?._id || composent.product || composent._id || composent.id || composent;
          return filterComponents.includes(composentProductId);
        });
        
        if (hasSelectedComposant) {
          productIdsWithSelectedComposants.push(product._id || product.id);
        }
      });
      
      // Filter tracking data to show only items with product_id in the gathered list
      filtered = filtered.filter(item => {
        if (!item.product_id) return false;
        const trackingProductId = item.product_id?.$oid || item.product_id?._id || item.product_id;
        return productIdsWithSelectedComposants.includes(trackingProductId);
      });
    }

    // Filter by price range
    if (filterPriceMin !== '') {
      const minPrice = parseFloat(filterPriceMin);
      filtered = filtered.filter(item => item.price >= minPrice);
    }
    if (filterPriceMax !== '') {
      const maxPrice = parseFloat(filterPriceMax);
      filtered = filtered.filter(item => item.price <= maxPrice);
    }

    // Search by name
    if (searchName.trim()) {
      const searchLower = searchName.toLowerCase();
      filtered = filtered.filter(item => 
        (item.name || '').toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [trackingData, filterActive, filterSynced, filterComponents, filterPriceMin, filterPriceMax, searchName, localProducts]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterActive, filterSynced, filterComponents, filterPriceMin, filterPriceMax, searchName]);

  const handleStoreChange = (option) => {
    setSelectedStoreId(option?.value || '');
    setTrackingData([]);
    setLocalProducts([]);
    setError('');
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilterActive('all');
    setFilterSynced('all');
    setFilterComponents([]);
    setFilterPriceMin('');
    setFilterPriceMax('');
    setSearchName('');
    setCurrentPage(1);
  };

  const handleShowComposants = (trackingItem) => {
    setSelectedTracking(trackingItem);
    setShowComposantsModal(true);
  };

  const handleCloseComposantsModal = () => {
    setShowComposantsModal(false);
    setSelectedTracking(null);
  };

  // Get composant products linked to the selected tracking item
  const linkedComposants = useMemo(() => {
    if (!selectedTracking?.product_id) return [];
    
    const productId = selectedTracking.product_id?.$oid || selectedTracking.product_id?._id || selectedTracking.product_id;
    const linkedProduct = localProducts.find(p => (p._id || p.id) === productId);
    if (!linkedProduct || !linkedProduct.composents) return [];

    // Get composant IDs from the linked product
    const composentIds = Array.isArray(linkedProduct.composents) 
      ? linkedProduct.composents.map(c => c._id || c.id || c)
      : [];

    // Find all composant products
    return localProducts.filter(p => {
      const productId = p._id || p.id;
      return p.isComposant === true && composentIds.includes(productId);
    });
  }, [selectedTracking, localProducts]);

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="mb-3">
            <i className="fas fa-chart-line me-2"></i>
            Tracking Glovo
          </h2>
        </div>
      </div>

      {/* Store Selector */}
      <div className="row mb-4">
        <div className="col-md-6">
          <label className="form-label fw-bold">Sélectionner un magasin</label>
          <Select
            options={storeOptions}
            value={storeOptions.find(opt => opt.value === selectedStoreId) || null}
            onChange={handleStoreChange}
            placeholder="Choisir un magasin..."
            isClearable
            isSearchable
          />
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      )}

      {selectedStoreId && !loading && (
        <>
          {/* Statistics Cards */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card bg-success text-white">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="card-subtitle mb-2">Produits Activés</h6>
                      <h2 className="card-title mb-0" style={{ fontWeight: 'bold', fontSize: '24pt', color: '#000' }}>{statistics.active}</h2>
                    </div>
                    <i className="fas fa-check-circle fa-3x opacity-50"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-secondary text-white">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="card-subtitle mb-2">Produits Désactivés</h6>
                      <h2 className="card-title mb-0" style={{ fontWeight: 'bold', fontSize: '24pt', color: '#000' }}>{statistics.inactive}</h2>
                    </div>
                    <i className="fas fa-times-circle fa-3x opacity-50"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-primary text-white">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="card-subtitle mb-2">Produits Sync</h6>
                      <h2 className="card-title mb-0" style={{ fontWeight: 'bold', fontSize: '24pt', color: '#000' }}>{statistics.synced}</h2>
                    </div>
                    <i className="fas fa-sync fa-3x opacity-50"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-warning text-dark">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="card-subtitle mb-2">Produits Anomalie</h6>
                      <h2 className="card-title mb-0" style={{ fontWeight: 'bold', fontSize: '24pt', color: '#000' }}>{statistics.anomalies}</h2>
                    </div>
                    <i className="fas fa-exclamation-triangle fa-3x opacity-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-filter me-2"></i>
                  Filtres
                </h5>
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={resetFilters}
                >
                  <i className="fas fa-redo me-1"></i>
                  Réinitialiser
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {/* Search by name */}
                <div className="col-md-3">
                  <label className="form-label">Rechercher par nom</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Nom du produit..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                  />
                </div>

                {/* Filter by active status */}
                <div className="col-md-2">
                  <label className="form-label">Statut</label>
                  <select
                    className="form-select"
                    value={filterActive}
                    onChange={(e) => setFilterActive(e.target.value)}
                  >
                    <option value="all">Tous</option>
                    <option value="active">Activé</option>
                    <option value="inactive">Désactivé</option>
                  </select>
                </div>

                {/* Filter by synced */}
                <div className="col-md-2">
                  <label className="form-label">Synchronisation</label>
                  <select
                    className="form-select"
                    value={filterSynced}
                    onChange={(e) => setFilterSynced(e.target.value)}
                  >
                    <option value="all">Tous</option>
                    <option value="synced">Synchronisé</option>
                    <option value="unsynced">Non synchronisé</option>
                  </select>
                </div>

                {/* Filter by composants */}
                <div className="col-md-4">
                  <label className="form-label">Filtrer par Composants</label>
                  <Select
                    isMulti
                    options={composantOptions}
                    value={composantOptions.filter(opt => filterComponents.includes(opt.value))}
                    onChange={(selected) => setFilterComponents(selected ? selected.map(s => s.value) : [])}
                    placeholder="Sélectionner des composants..."
                    isClearable
                    isSearchable
                    formatOptionLabel={(option) => (
                      <div className="d-flex align-items-center gap-2">
                        {option.image ? (
                          <img 
                            src={option.image} 
                            alt={option.label}
                            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        ) : (
                          <div 
                            className="bg-secondary rounded d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <i className="fas fa-puzzle-piece text-white"></i>
                          </div>
                        )}
                        <div>
                          <div>{option.label}</div>
                          <small className="text-muted">{option.price} MAD</small>
                        </div>
                      </div>
                    )}
                  />
                </div>

                {/* Price range */}
                <div className="col-md-2">
                  <label className="form-label">Fourchette de prix</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Min"
                      value={filterPriceMin}
                      onChange={(e) => setFilterPriceMin(e.target.value)}
                      step="0.01"
                    />
                    <span className="input-group-text">-</span>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Max"
                      value={filterPriceMax}
                      onChange={(e) => setFilterPriceMax(e.target.value)}
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results Table */}
          <div className="card">
            <div className="card-header bg-light">
              <h5 className="mb-0">
                <i className="fas fa-list me-2"></i>
                Produits Glovo ({filteredData.length})
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover table-striped mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: '120px' }}>Image</th>
                      <th>Glovo ID</th>
                      <th>Nom</th>
                      <th>Prix</th>
                      <th>Statut</th>
                      <th>Sync</th>
                      <th>Product ID</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center py-4 text-muted">
                          <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                          Aucun produit trouvé
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((item) => {
                        // Extract the actual product ID from the object
                        const productId = item.product_id?.$oid || item.product_id?._id || item.product_id;
                        const localProduct = productId 
                          ? localProducts.find(p => (p._id || p.id) === productId)
                          : null;
                        const hasAnomaly = localProduct && item.price < (localProduct.regular_price || 0);

                        // Get image URL: if synced and no imageUrl, use local product image
                        const imageUrl = item.imageUrl || (item.synced && localProduct?.url_product ? `${process.env.REACT_APP_DATA_PIC_PATH}/${localProduct.url_product}` : null);
                        // Get price: if synced and no price, use local product regular_price
                        const displayPrice = item.price || (item.synced && localProduct?.regular_price ? localProduct.regular_price : 0);

                        return (
                          <tr key={item._id} className={hasAnomaly ? 'table-warning' : ''}>
                            <td>
                              {imageUrl ? (
                                <img 
                                  src={imageUrl} 
                                  alt={item.name}
                                  style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                  className="rounded"
                                />
                              ) : (
                                <div 
                                  className="bg-secondary rounded d-flex align-items-center justify-content-center"
                                  style={{ width: '100px', height: '100px' }}
                                >
                                  <i className="fas fa-image text-white fa-2x"></i>
                                </div>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">{item.glovo_id}</small>
                            </td>
                            <td>
                              <strong>{item.name}</strong>
                              {hasAnomaly && (
                                <div className="text-warning small">
                                  <i className="fas fa-exclamation-triangle me-1"></i>
                                  Prix Glovo ({item.price}) &lt; Prix local ({localProduct.regular_price})
                                </div>
                              )}
                            </td>
                            <td>
                              <strong>{displayPrice} MAD</strong>
                            </td>
                            <td>
                              {item.isactive ? (
                                <span className="badge bg-success">
                                  <i className="fas fa-check me-1"></i>
                                  Activé
                                </span>
                              ) : (
                                <span className="badge bg-secondary">
                                  <i className="fas fa-times me-1"></i>
                                  Désactivé
                                </span>
                              )}
                            </td>
                            <td>
                              {item.synced ? (
                                <span className="badge bg-primary">
                                  <i className="fas fa-check-circle me-1"></i>
                                  Sync
                                </span>
                              ) : (
                                <span className="badge bg-warning text-dark">
                                  <i className="fas fa-clock me-1"></i>
                                  En attente
                                </span>
                              )}
                            </td>
                            <td>
                              {item.product_id ? (
                                <span className="badge bg-info text-dark">
                                  <i className="fas fa-link me-1"></i>
                                  Lié
                                </span>
                              ) : (
                                <span className="badge bg-light text-dark">
                                  <i className="fas fa-unlink me-1"></i>
                                  Non lié
                                </span>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(item.date || item.createdAt).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </small>
                            </td>
                            <td>
                              {item.product_id && (
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleShowComposants(item)}
                                  title="Voir les composants"
                                >
                                  <i className="fas fa-puzzle-piece me-1"></i>
                                  Composants
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {filteredData.length > 0 && (
              <div className="card-footer">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center gap-2">
                      <label className="mb-0">Afficher:</label>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                      </select>
                      <span className="text-muted">
                        Affichage de {((currentPage - 1) * itemsPerPage) + 1} à {Math.min(currentPage * itemsPerPage, filteredData.length)} sur {filteredData.length} produits
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <nav>
                      <ul className="pagination justify-content-end mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                          >
                            <i className="fas fa-angle-double-left"></i>
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            <i className="fas fa-angle-left"></i>
                          </button>
                        </li>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            <i className="fas fa-angle-right"></i>
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            <i className="fas fa-angle-double-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Composants Modal */}
          {showComposantsModal && selectedTracking && (
            <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-puzzle-piece me-2"></i>
                      Composants - {selectedTracking.name}
                    </h5>
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={handleCloseComposantsModal}
                    ></button>
                  </div>
                  <div className="modal-body">
                    {linkedComposants.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-puzzle-piece fa-3x mb-3 d-block opacity-25"></i>
                        <p>Aucun composant lié à ce produit</p>
                      </div>
                    ) : (
                      <div className="row g-3">
                        {linkedComposants.map((composant) => (
                          <div key={composant._id || composant.id} className="col-md-6">
                            <div className="card h-100">
                              <div className="row g-0">
                                <div className="col-4">
                                  {composant.image || composant.imageUrl ? (
                                    <img 
                                      src={composant.image || composant.imageUrl} 
                                      alt={composant.name}
                                      className="img-fluid rounded-start h-100 w-100"
                                      style={{ objectFit: 'cover' }}
                                    />
                                  ) : (
                                    <div 
                                      className="bg-secondary rounded-start h-100 d-flex align-items-center justify-content-center"
                                    >
                                      <i className="fas fa-puzzle-piece fa-3x text-white opacity-50"></i>
                                    </div>
                                  )}
                                </div>
                                <div className="col-8">
                                  <div className="card-body">
                                    <h6 className="card-title">{composant.name}</h6>
                                    {composant.price && (
                                      <p className="card-text">
                                        <strong>{composant.price} MAD</strong>
                                      </p>
                                    )}
                                    <div className="d-flex gap-1 flex-wrap">
                                      <span className="badge bg-success">
                                        <i className="fas fa-check me-1"></i>
                                        Composant
                                      </span>
                                      <span className="badge bg-info text-dark">
                                        <i className="fas fa-link me-1"></i>
                                        Lié
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={handleCloseComposantsModal}
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default GlovoTracking;
